import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { Menu, MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { FacturaEncabezado } from 'src/app/domain/model/servicios-entregados/factura-encabezado.model';

@Component({
  selector: 'app-facturas-resultados',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, TableModule, ButtonModule, DropdownModule, TooltipModule, MenuModule],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-card header="Resultados" styleClass="mb-4">
      @if (totalRegistros() > 0) {
        <div class="flex justify-between items-center mb-3">
          <span class="text-sm text-gray-600">
            Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de {{ totalRegistros() }}
          </span>
        </div>
      }

      <div class="md:hidden space-y-3">
        @if (!loading() && facturas().length === 0) {
          <div class="text-center p-4 text-gray-600 border border-gray-200 dark:border-gray-700 rounded-lg">
            Ingrese criterios de búsqueda y haga clic en "Buscar"
          </div>
        }

        @for (factura of facturas(); track factura.nmconsecutivo ?? $index) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
            <div class="grid grid-cols-1 gap-2 text-sm">
              <div><span class="text-gray-500">Número:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.cdfactura || '-' }}</span></div>
              <div><span class="text-gray-500">Fecha:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.fefactura | date: 'yyyy-MM-dd' }}</span></div>
              <div><span class="text-gray-500">Servicio:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.servicioFacturadoDescripcion || '-' }}</span></div>
              <div><span class="text-gray-500">Convenio:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.convenioDescripcion || '-' }}</span></div>
              <div><span class="text-gray-500">Oficina:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.oficinaDescripcion || '-' }}</span></div>
              <div><span class="text-gray-500">Estado:</span> <span class="text-gray-800 dark:text-gray-200">{{ factura.estadoDescripcion || '-' }}</span></div>
            </div>

            <div class="mt-3 grid grid-cols-1 gap-2">
              @if (mostrarAccionEditar()(factura)) {
                <p-button label="Editar" icon="pi pi-pencil" severity="secondary" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" (onClick)="editar.emit(factura)"></p-button>
              }
              @if (mostrarAccionVerDetalle()(factura)) {
                <p-button label="Ver Detalle" icon="pi pi-eye" severity="secondary" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" (onClick)="verDetalle.emit(factura)"></p-button>
              }
              @if (mostrarAccionAgregarDetalle()(factura)) {
                <p-button [label]="labelAgregarDetalle()(factura)" icon="pi pi-list" severity="secondary" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" (onClick)="agregarDetalle.emit(factura)"></p-button>
              }
              @if (mostrarAccionEnviarCliente()(factura)) {
                <p-button label="Enviar Cliente" icon="pi pi-send" size="small" styleClass="w-full min-h-[44px]" (onClick)="enviarCliente.emit(factura)"></p-button>
              }
              @if (mostrarAccionEliminar()(factura)) {
                <p-button label="Eliminar" icon="pi pi-trash" severity="danger" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" (onClick)="eliminar.emit(factura)"></p-button>
              }
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
      <p-table
        [resizableColumns]="true"
        columnResizeMode="expand"
        [value]="facturas()"
        [paginator]="false"
        [rows]="pageSize()"
        responsiveLayout="scroll"
        styleClass="tabla-servicios-entregados p-datatable-sm p-datatable-gridlines"
        [scrollable]="true"
        [tableStyle]="{ 'min-width': '72rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn>Número de Factura</th>
            <th pResizableColumn>Fecha</th>
            <th pResizableColumn><span class="grid-header-text" title="Servicio Facturado">Servicio Facturado</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Convenio">Convenio</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Oficina">Oficina</span></th>
            <th pResizableColumn>Estado</th>
            <th pResizableColumn>Acciones</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-factura>
          <tr>
            <td>{{ factura.cdfactura }}</td>
            <td>{{ factura.fefactura | date: 'yyyy-MM-dd' }}</td>
            <td><span class="grid-cell-text" [attr.title]="factura.servicioFacturadoDescripcion">{{ factura.servicioFacturadoDescripcion }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="factura.convenioDescripcion">{{ factura.convenioDescripcion }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="factura.oficinaDescripcion">{{ factura.oficinaDescripcion }}</span></td>
            <td>{{ factura.estadoDescripcion }}</td>
            <td>
              @let editarVisible = mostrarAccionEditar()(factura);
              @let verDetalleVisible = mostrarAccionVerDetalle()(factura);
              @let eliminarVisible = mostrarAccionEliminar()(factura);
              @let enviarClienteVisible = mostrarAccionEnviarCliente()(factura);
              @let agregarDetalleVisible = mostrarAccionAgregarDetalle()(factura);
              @let algunaAccion = editarVisible || verDetalleVisible || eliminarVisible
                || enviarClienteVisible || agregarDetalleVisible;

              <div class="flex items-center gap-2 whitespace-nowrap">
                @if (algunaAccion) {
                  <button
                    pButton
                    type="button"
                    icon="pi pi-ellipsis-v"
                    severity="secondary"
                    [text]="true"
                    size="small"
                    styleClass="min-w-[44px] min-h-[44px]"
                    pTooltip="Más acciones"
                    (click)="abrirMenuAcciones(factura, accionesMenu, $event)"
                    [disabled]="loading()"
                    [attr.aria-label]="'Más acciones para ' + factura.cdfactura"
                  ></button>
                }
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="text-center py-4">
              @if (loading()) {
                <span>Cargando...</span>
              } @else if (facturas().length === 0 && totalRegistros() === 0) {
                <div class="flex flex-col items-center gap-2 py-6">
                  <i class="pi pi-search text-4xl text-gray-400"></i>
                  <span class="text-gray-600">Ingrese criterios de búsqueda y haga clic en "Buscar"</span>
                </div>
              } @else {
                <span>No se encontraron facturas con los criterios de búsqueda.</span>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
      </div>

      <p-menu #accionesMenu [popup]="true" [model]="menuItems" appendTo="body"></p-menu>

      @if (totalRegistros() > 0) {
        <div class="flex justify-between items-center p-3 border-t mt-2">
          <div class="text-gray-600 text-sm">
            Página {{ currentPage() }} de {{ totalPages() }}
          </div>
          <div class="flex gap-2 items-center flex-wrap justify-end sm:justify-start">
            <span class="text-sm text-gray-600">Registros:</span>
            <p-dropdown
              [options]="pageSizeOptions()"
              [ngModel]="selectedPageSize()"
              (ngModelChange)="selectedPageSizeChange.emit($event)"
              [style]="{ width: '80px' }"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
            ></p-dropdown>
            <button
              pButton
              icon="pi pi-angle-double-left"
              class="p-button-sm min-w-[44px] min-h-[44px]"
              [disabled]="currentPage() <= 1"
              (click)="irPrimeraPagina.emit()"
              pTooltip="Primera página"
            ><span class="sr-only">Ir a primera página</span></button>
            <button
              pButton
              icon="pi pi-angle-left"
              class="p-button-sm min-w-[44px] min-h-[44px]"
              [disabled]="!hasPrevious()"
              (click)="irPaginaAnterior.emit()"
              pTooltip="Página anterior"
            ><span class="sr-only">Ir a página anterior</span></button>
            <button
              pButton
              icon="pi pi-angle-right"
              class="p-button-sm min-w-[44px] min-h-[44px]"
              [disabled]="!hasNext()"
              (click)="irPaginaSiguiente.emit()"
              pTooltip="Página siguiente"
            ><span class="sr-only">Ir a página siguiente</span></button>
            <button
              pButton
              icon="pi pi-angle-double-right"
              class="p-button-sm min-w-[44px] min-h-[44px]"
              [disabled]="currentPage() >= totalPages()"
              (click)="irUltimaPagina.emit()"
              pTooltip="Última página"
            ><span class="sr-only">Ir a última página</span></button>
          </div>
        </div>
      }
    </p-card>
  `
})
export class FacturasResultadosComponent {
  facturas = input<FacturaEncabezado[]>([]);
  loading = input(false);
  totalRegistros = input(0);
  pageSize = input(10);
  currentPage = input(1);
  totalPages = input(0);
  registroInicio = input(0);
  registroFin = input(0);
  hasPrevious = input(false);
  hasNext = input(false);
  pageSizeOptions = input<Array<{ label: string; value: number }>>([]);
  selectedPageSize = input(10);

  mostrarAccionEditar = input.required<(factura: FacturaEncabezado) => boolean>();
  mostrarAccionVerDetalle = input.required<(factura: FacturaEncabezado) => boolean>();
  mostrarAccionEliminar = input.required<(factura: FacturaEncabezado) => boolean>();
  mostrarAccionEnviarCliente = input.required<(factura: FacturaEncabezado) => boolean>();
  mostrarAccionAgregarDetalle = input.required<(factura: FacturaEncabezado) => boolean>();
  labelAgregarDetalle = input.required<(factura: FacturaEncabezado) => string>();

  editar = output<FacturaEncabezado>();
  verDetalle = output<FacturaEncabezado>();
  eliminar = output<FacturaEncabezado>();
  enviarCliente = output<FacturaEncabezado>();
  agregarDetalle = output<FacturaEncabezado>();

  selectedPageSizeChange = output<number>();
  irPrimeraPagina = output<void>();
  irPaginaAnterior = output<void>();
  irPaginaSiguiente = output<void>();
  irUltimaPagina = output<void>();

  menuItems: MenuItem[] = [];

  abrirMenuAcciones(factura: FacturaEncabezado, menu: Menu, event: Event): void {
    this.menuItems = this.construirMenuItems(factura);
    menu.toggle(event);
  }

  private construirMenuItems(factura: FacturaEncabezado): MenuItem[] {
    const items: MenuItem[] = [];

    if (this.mostrarAccionEditar()(factura)) {
      items.push({
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.editar.emit(factura)
      });
    }

    if (this.mostrarAccionVerDetalle()(factura)) {
      items.push({
        label: 'Ver Detalle',
        icon: 'pi pi-eye',
        command: () => this.verDetalle.emit(factura)
      });
    }

    if (this.mostrarAccionAgregarDetalle()(factura)) {
      items.push({
        label: this.labelAgregarDetalle()(factura),
        icon: 'pi pi-list',
        command: () => this.agregarDetalle.emit(factura)
      });
    }

    if (this.mostrarAccionEnviarCliente()(factura)) {
      items.push({
        label: 'Enviar Cliente',
        icon: 'pi pi-send',
        command: () => this.enviarCliente.emit(factura)
      });
    }

    if (this.mostrarAccionEliminar()(factura)) {
      if (items.length > 0) {
        items.push({ separator: true });
      }
      items.push({
        label: 'Eliminar',
        icon: 'pi pi-trash',
        styleClass: 'text-red-600',
        command: () => this.eliminar.emit(factura)
      });
    }

    return items;
  }
}
